<template>
  <form novalidate @submit.prevent="reactivateService">
    <BLoading :active.sync="isLoading" />
    <div v-if="!isLoading" class="container">
      <div v-if="noAvailableCycles">
        <h1>Resume lawn mowing services</h1>
        <p>Please contact {{ brandName }} for further assistance.</p>
      </div>
      <div v-else class="columns reactivate-view--reverse-row-order">
        <div class="column is-6">
          <div class="reactivate-view--main-header">
            <h1>Resume lawn mowing services</h1>
            <RouterLink
              class="reactivate-view--different-property"
              :to="{ name: 'services' }"
              track-id="reactivate-view--different-property"
              test-id="different_property"
            >
              <span>Need service at a different property?</span>
            </RouterLink>
          </div>

          <div class="reactivate-view--cycle-container">
            <LsClickable
              v-for="cycle in cycleOptions"
              :key="cycle.value.price_id"
              :class="
                cycle.value.cycle === selectedFrequency.cycle
                  ? 'reactivate-view--selected-cycle-item'
                  : 'reactivate-view--cycle-item'
              "
              track-id="reactivate_view-ls_select_frequency"
              @click="selectFrequency(cycle)"
            >
              <div
                :class="
                  cycle.value.cycle === selectedFrequency.cycle
                    ? 'reactivate-view--selected-cycle-text'
                    : 'reactivate-view--cycle-text'
                "
              >
                <span>
                  <strong>{{ cycle.label.frequency }}</strong>
                </span>
                <span> {{ cycle.label.price }} </span>
              </div>
            </LsClickable>
          </div>
          <div class="reactivate-view--start-date">
            <p class="reactivate-view--preferred-start-date">
              <strong>Preferred Start Date</strong>
              <span class="reactivate-view--question-circle">
                <BTooltip
                  label="A minimum of 2 business days is required to schedule your first service"
                  position="is-left"
                  multilined
                  >?
                </BTooltip>
              </span>
            </p>
          </div>
          <LsDatepicker
            v-model="inputs.start_date"
            track-id="reactivate_view-preferred_start_date"
            test-id="preferred_start_date"
            :validation="{ required: true }"
            name="preferred_start_date"
            :min-date="minDate"
            :max-date="maxDate"
            class="reactivate-view--preferred-start-datepicker"
            :unselectable-days-of-week="unselectableDays"
          />

          <div class="reactivate-view--payment">
            <h2 class="reactivate-view--payment-header">Payment</h2>
            <div
              v-if="last4 && !newCCRequired"
              class="reactivate-view--payment-last-4"
              test-id="current-payment"
            >
              <div class="reactivate-view--credit-card">
                <LsIcon type="creditcard" size="lg" color="primary-light" />
                <span class="reactivate-view--card-ending-last-4">
                  Card ending in <strong>{{ last4 }}</strong></span
                >
              </div>

              <LsButton
                text="update"
                track-id="reactivate_view-update"
                bg-color="primary-light"
                text-color="primary"
                class="reactivate-view--update-credit-card-button"
                test-id="updatePayment"
                @click="openPayment"
              />
            </div>
            <div v-if="!last4 || newCCRequired" test-id="new-payment">
              <div class="columns">
                <div class="column">
                  <label>Card Number</label>
                  <div id="card-number" class="stripe-card-input"></div>
                </div>
              </div>
              <div class="columns">
                <div class="column">
                  <div class="columns is-mobile">
                    <div class="column">
                      <label>Exp. Month/Year</label>
                      <div id="card-expiry" class="stripe-card-input"></div>
                    </div>
                  </div>
                </div>
                <div class="column">
                  <label>CVC (on back)</label>
                  <div id="card-cvc" class="stripe-card-input"></div>
                </div>
              </div>
            </div>
          </div>
          <div class="reactivate-view--itemized-charges">
            <div class="reactivate-view--charges-text">
              <span>{{ mowingFrequency }}</span>
              <span>
                <strong>{{ inputs.cycle.price | currency }}</strong>
              </span>
            </div>

            <div v-if="discount_amount" class="reactivate-view--discount-text">
              <span>Discount</span>
              <span>
                <strong>{{ discount_amount }}</strong>
              </span>
            </div>

            <div class="reactivate-view--charges-text">
              <div class="reactivate-view--trust-safety-fee">
                <span>+ Trust &amp; Safety Fee</span>
                <span class="reactivate-view--question-circle">
                  <BTooltip
                    :label="
                      `This fee helps pay for ${brandName}'s insurance policy and provider vetting`
                    "
                    position="is-top"
                    multilined
                    >?
                  </BTooltip>
                </span>
              </div>

              <span
                ><strong>{{ trustFee }}</strong></span
              >
            </div>
          </div>
          <div class="reactivate-view--charges-total">
            <div class="reactivate-view--total-summary">
              <span class="reactivate-view--total-text">Total</span>
              <h1 test-id="mowing" class="reactivate-view--total-amount">{{ total_amount }}</h1>
            </div>
            <div class="reactivate-view--per-service-text">{{ mowingPrice.perServiceText }}</div>
            <div class="reactivate-view--checkbox">
              <BCheckbox
                v-model="checkbox"
                test-id="ls-checkbox"
                name="long_grass"
                type="is-primary-invert"
                :disabled="isLoading"
              >
                I agree to {{ brandName }}'s
                <LsClickable
                  class="reactivate-view--long-grass-policy"
                  track-id="reactivate_view-long_grass_policy_link"
                  @click="openLongGrassModal"
                >
                  <span>long grass policy</span>
                </LsClickable>
              </BCheckbox>
            </div>
            <p v-if="checkbox === false" class="help is-error">This is required</p>
          </div>

          <LsBalanceWillBeProcessedNotice
            test-id="reactivate_view--balance_will_be_processed_notice"
            origin="reactivate_view"
            :balance="getAccountBalance"
          />

          <LsButton
            text="Complete booking"
            type="submit"
            track-id="reactivate_view-complete_booking"
            :loading="reactivation.loadingStatus.isUpdating || profile.loadingStatus.isUpdating"
          />
        </div>

        <div class="column is-offset-1">
          <LsAddressCard :property="selectedProperty" class="reactivate-view--address-card" />
          <div v-if="discount_amount" test-id="special-deal" class="reactivate-view--special_deal">
            <h1>Special Deal</h1>
            <p>
              <strong>Welcome back to {{ brandName }}!</strong>
            </p>
            <p>Resume service today and get your next mow for only {{ total_amount }}</p>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import _ from 'lodash';
import moment from 'moment';
import {
  REACTIVATION__GET_PRICING_AND_SELLABLE_DAYS,
  REACTIVATION__UNSELECTABLE_DAYS,
  REACTIVATION__REACTIVATE_SERVICE,
  REACTIVATION__VALIDATE_DEAL,
  PROPERTIES__SELECTED_PROPERTY,
  PROPERTIES__GET_PROPERTIES_AND_SCHEDULES,
  PROFILE__UPDATE_PAYMENT,
} from '@/store/types';
import { loadStripe } from '@stripe/stripe-js';
import {
  lsDateHelpers,
  lsFilters,
  lsModalService,
  lsRouterHelpers,
  lsStorageService,
  lsStorageServiceConstants,
  lsValidationService,
} from 'ls-vue-common';
import { lsMarketHelpers } from '@/helpers';
import { LsDatepicker, LsAddressCard, LsButton, LsIcon, LsClickable } from '@/components/common';
import { LongGrassPolicyModal } from '@/modals';
import { themedComponentMixin } from '@/mixins';
import { LsBalanceWillBeProcessedNotice } from '@/components';
import { PROPERTY_DEACTIVATED_REASONS } from '@/constants/property';
import { CYCLE_MONTHLY } from '@/constants';

export default {
  name: 'ReactivateView',
  $_veeValidate: {
    validator: 'new',
  },
  components: {
    LsDatepicker,
    LsAddressCard,
    LsButton,
    LsIcon,
    LsClickable,
    LsBalanceWillBeProcessedNotice,
  },
  mixins: [themedComponentMixin],
  data() {
    return {
      property_id: lsRouterHelpers.getParam(this.$route, 'property_id'),
      deal: lsRouterHelpers.getQuery(this.$route, 'deal'),
      checkbox: null,
      cycleOptions: [],
      mowingPrice: {},
      inputs: {
        cycle: {},
        start_date: moment().toDate(),
      },
      stripe: null,
      stripeElements: null,
      card: null,
      token: null,
      cardNumber: null,
      cardExpiry: null,
      cardCvc: null,
    };
  },
  computed: {
    ...mapState(['reactivation', 'auth', 'properties', 'profile']),
    ...mapGetters([PROPERTIES__SELECTED_PROPERTY, REACTIVATION__UNSELECTABLE_DAYS]),
    discount_amount() {
      const { discount_amount } = this.inputs.cycle;
      return discount_amount ? lsFilters.currency({ amount: discount_amount }) : null;
    },
    total_amount() {
      const { total_amount } = this.inputs.cycle;
      return lsFilters.currency({ amount: total_amount });
    },
    deal_id() {
      if (this.deal) {
        this.saveDealToLocalStorage(this.deal);
        return this.deal;
      } else {
        return this.getDealFromLocalStorage();
      }
    },
    isLoading() {
      return this.properties.loadingStatus.isLoading || this.reactivation.loadingStatus.isLoading;
    },
    minDate() {
      return lsDateHelpers.getFirstAvailDate({
        sellableDays: this.reactivation.sellableDays,
      });
    },
    maxDate() {
      return moment(this.minDate)
        .add(1, 'years')
        .toDate();
    },
    last4() {
      return this.auth.user.customer.last4;
    },
    newCCRequired() {
      return (
        _.isEmpty(this.last4) ||
        (!_.isNil(this.selectedProperty) &&
          this.selectedProperty.can_self_reactivate_reason ===
            PROPERTY_DEACTIVATED_REASONS.PAYMENT_FAILURE)
      );
    },
    selectedProperty() {
      return this[PROPERTIES__SELECTED_PROPERTY](this.property_id);
    },
    getMonths() {
      return lsDateHelpers.getArrayOfMonths();
    },
    getYears() {
      return lsDateHelpers.getArrayOfNext20Years();
    },
    trustFee() {
      const { trust_fee } = this.inputs.cycle;
      return lsFilters.currency({ amount: trust_fee });
    },
    noAvailableCycles() {
      return _.isEmpty(this.cycleOptions);
    },
    mowingFrequency() {
      return `${lsFilters.frequency({
        cycle: this.inputs.cycle.cycle,
      })} Mowing`;
    },
    selectedFrequency() {
      return this.inputs.cycle;
    },
    unselectableDays() {
      return this[REACTIVATION__UNSELECTABLE_DAYS];
    },
    getAccountBalance() {
      const { customer } = this.auth.user;
      return (customer || {}).balance || 0;
    },
    cannotSelfReactivate() {
      return (
        _.isNil(this.selectedProperty) ||
        this.selectedProperty.is_active ||
        !this.selectedProperty.can_self_reactivate
      );
    },
    isDeactivatedDueToPaymentFailure() {
      return (
        this.selectedProperty &&
        this.selectedProperty.can_self_reactivate_reason ===
          PROPERTY_DEACTIVATED_REASONS.PAYMENT_FAILURE
      );
    },
  },
  async created() {
    // strip out qp
    this.$router.replace({});
    await Promise.all([
      this[PROPERTIES__GET_PROPERTIES_AND_SCHEDULES](),
      this[REACTIVATION__GET_PRICING_AND_SELLABLE_DAYS]({
        property_id: this.property_id,
        deal_id: this.deal_id,
      }),
    ]);

    if (this.cannotSelfReactivate && !this.isDeactivatedDueToPaymentFailure) {
      return this.$router.push({
        name: 'services',
      });
    }

    this.getCycleOptions();
    this.inputs = {
      cycle: this.cycleOptions[0].value,
      start_date: this.minDate,
    };

    if (!this.last4 || this.newCCRequired) {
      this.stripe = await loadStripe(process.env.VUE_APP_STRIPE_KEY);
      this.stripeElements = this.stripe.elements();
      const style = {
        base: {
          color: '#4a4a4a',
          fontFamily: 'Montserrat,Roboto,Arial,sans-serif',
          fontSmoothing: 'antialiased',
          fontSize: '14px',
          '::placeholder': {
            color: '#aab7c4',
          },
        },
        invalid: {
          color: '#fa755a',
          iconColor: '#fa755a',
        },
      };
      this.cardNumber = this.stripeElements.create('cardNumber', { style });
      this.cardNumber.mount('#card-number');
      this.cardExpiry = this.stripeElements.create('cardExpiry', { style });
      this.cardExpiry.mount('#card-expiry');
      this.cardCvc = this.stripeElements.create('cardCvc', { style });
      this.cardCvc.mount('#card-cvc');
    }
  },
  methods: {
    ...mapActions([
      REACTIVATION__GET_PRICING_AND_SELLABLE_DAYS,
      REACTIVATION__REACTIVATE_SERVICE,
      REACTIVATION__VALIDATE_DEAL,
      PROPERTIES__GET_PROPERTIES_AND_SCHEDULES,
      PROFILE__UPDATE_PAYMENT,
    ]),
    saveDealToLocalStorage() {
      lsStorageService.set(lsStorageServiceConstants.REACTIVATION_DEAL, this.deal);
      lsStorageService.set(
        lsStorageServiceConstants.REACTIVATION_DEAL_STORED_AT,
        moment().valueOf()
      );
    },
    getDealFromLocalStorage() {
      // returns deal if exists in localStorage and is less than 48 hours old
      const storedAt = lsStorageService.get(lsStorageServiceConstants.REACTIVATION_DEAL_STORED_AT);
      const reactivationDeal = lsStorageService.get(lsStorageServiceConstants.REACTIVATION_DEAL);
      const hasBeenLessThan48Hours = moment().diff(_.toNumber(storedAt), 'hours') < 48;
      const isValidDeal = hasBeenLessThan48Hours && reactivationDeal;

      return isValidDeal ? reactivationDeal : null;
    },
    openPayment() {
      const name = 'profileUpdatePayment';
      this.$router.push({ name });
    },
    openLongGrassModal() {
      lsModalService.open({
        parent: this,
        component: LongGrassPolicyModal,
      });
    },
    selectFrequency(cycle) {
      this.inputs.cycle = cycle.value;
    },
    getCycleOptions() {
      const excludeMonthlyMowing = lsMarketHelpers.isMonthlyMowingDisabled(
        _.get(this.selectedProperty, 'market')
      );
      const availableCycles = _.filter(this.reactivation.availableCycles, (availableCycle) => {
        if (availableCycle.cycle === CYCLE_MONTHLY && excludeMonthlyMowing) {
          return false;
        }

        return true;
      });

      this.cycleOptions = _.map(availableCycles, (availableCycle) => {
        return {
          label: {
            frequency: `${lsFilters.frequency({ cycle: availableCycle.cycle })}`,
            price: `${lsFilters.currency({
              amount: availableCycle.price,
              discount_amount: availableCycle.discount_amount,
              total_amount: availableCycle.total_amount,
              trimTrailingZeros: true,
            })}`,
          },
          value: availableCycle,
        };
      });
    },
    updateCheckboxValidation() {
      if (this.checkbox === null) {
        this.checkbox = false;
      }
    },
    async reactivateService() {
      this.updateCheckboxValidation();
      const valid = await this.$validator.validateAll();
      if (!valid || !this.checkbox) {
        return lsValidationService.scrollToInvalid();
      }

      let requestPromise = Promise.resolve();

      if (this.newCCRequired) {
        requestPromise = this[PROFILE__UPDATE_PAYMENT]({
          stripe: this.stripe,
          cardNumber: this.cardNumber,
          onSuccess: _.noop,
        });
      }

      requestPromise.then(() => {
        if (this.newCCRequired) {
          return;
        }

        this[REACTIVATION__REACTIVATE_SERVICE]({
          property_id: this.property_id,
          start_date: this.inputs.start_date,
          cycle: this.inputs.cycle.cycle,
          deal: this.deal_id,
          onSuccess: () =>
            this.$router.replace({
              name: 'reactivatePolicies',
              params: {
                property_id: this.property_id,
              },
            }),
        });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.reactivate-view--trust-safety-fee {
  display: flex;
  align-items: center;
  font-size: $size-5;
}
.reactivate-view--preferred-start-date {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: $size-5;
}
.reactivate-view--long-grass-policy {
  color: $blue;
}
.reactivate-view--start-date {
  margin-bottom: 10px;
}
.reactivate-view--question-circle {
  margin-left: 10px;
  color: $white;
  height: 26px;
  width: 26px;
  background: $blue;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  cursor: pointer;
}
.reactivate-view--preferred-start-datepicker {
  margin-bottom: 30px;
  padding-bottom: 30px;
  border-bottom: 1px solid $primary-light;
}
.reactivate-view--credit-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.reactivate-view--card-ending-last-4 {
  margin-left: 10px;
}
.reactivate-view--payment-last-4 {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 10px 0;
}
.reactivate-view--payment {
  margin-bottom: 30px;
  padding-bottom: 30px;
  border-bottom: 1px solid $primary-light;
}
.reactivate-view--payment-header {
  margin-bottom: 20px;
  font-weight: bold;
}

.reactivate-view--checkbox {
  display: flex;
}
.reactivate-view--cycle-container {
  display: flex;
  justify-content: flex-start;
  margin: 30px auto;
}
.reactivate-view--cycle-item {
  background-color: $primary-light;
  color: $primary;
  width: 150px;
  margin-right: 5px;
  align-items: center;
  justify-content: space-between;
  strong {
    color: $primary;
  }
}
.reactivate-view--cycle-item:last-child {
  margin-right: 0;
}
.reactivate-view--selected-cycle-item {
  background-color: $primary;
  color: $white;
  width: 150px;
  margin-right: 5px;
  align-items: center;
  justify-content: space-between;
}
.reactivate-view--selected-cycle-text {
  color: $white;
  font-size: 18px;
  display: flex;
  padding: 20px 15px;
  justify-content: space-between;
  flex-direction: column;
  strong {
    color: $white;
  }
}
.reactivate-view--cycle-text {
  color: $primary;
  font-size: 18px;
  display: flex;
  padding: 20px 15px;
  justify-content: space-between;
  flex-direction: column;
}
.reactivate-view--address-card {
  margin-bottom: 30px;
  padding-bottom: 30px;
  border-bottom: 1px solid $primary-light;
}
.reactivate-view--itemized-charges {
  margin-bottom: 30px;
  padding-bottom: 30px;
  border-bottom: 1px solid $primary-light;
}
.reactivate-view--charges-text {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}
.reactivate-view--discount-text {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}
.reactivate-view--charges-total {
  margin-bottom: 30px;
  padding-bottom: 30px;
  border-bottom: 1px solid $primary-light;
}
.reactivate-view--per-service-text {
  text-align: right;
  font-size: 12px;
  margin-bottom: 20px;
  padding-bottom: 20px;
}
.reactivate-view--total-summary {
  text-align: right;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}
.reactivate-view--total-text {
  color: $primary;
  margin-right: 15px;
}
.reactivate-view--total-amount {
  font-weight: bold;
}

label {
  font-weight: bold;
  font-size: 1.4rem;
  color: #4a4a4a;
  margin-bottom: 0.5em;
  font-family: 'Montserrat', 'Roboto', Arial, sans-serif;
}

.stripe-card-input {
  border: 1px solid;
  border-color: $theme-color-primary;
  padding: 6px;
}
@media (max-width: $mobile-max-width) {
  .reactivate-view--reverse-row-order {
    display: flex;
    flex-direction: column-reverse;
  }
  .reactivate-view--main-header {
    padding-bottom: 15px;
    margin-bottom: 15px;
    border-bottom: 1px solid $primary-light;
  }
  .reactivate-view--card-ending-last-4 {
    margin-left: 10px;
    font-size: $size-6;
  }
  .reactivate-view--credit-card {
    flex-basis: auto;
  }
  .reactivate-view--update-credit-card-button {
    min-width: 0;
    flex-basis: 40%;
  }
  .reactivate-view--cycle-container {
    flex-direction: column;
    margin-top: 0;
    margin-bottom: 20px;
    padding-bottom: 20px;
    border-bottom: 1px solid $primary-light;
  }
  .reactivate-view--cycle-item,
  .reactivate-view--selected-cycle-item {
    margin: 5px auto;
    width: 100%;
    padding: 10px 0;
  }
  .reactivate-view--cycle-text,
  .reactivate-view--selected-cycle-text {
    flex-direction: row;
    padding: 20px;
  }
  .reactivate-view--preferred-start-datepicker {
    margin-bottom: 20px;
  }
  .reactivate-view--address-card {
    margin-bottom: 20px;
    padding-bottom: 20px;
    border-bottom: 1px solid $primary-light;
  }
  .reactivate-view--special_deal {
    padding-bottom: 20px;
    border-bottom: 1px solid $primary-light;
  }
}
.themed-app {
  .reactivate-view--trust-safety-fee {
    font-size: $theme-txt-body;
  }
  .reactivate-view--preferred-start-date {
    font-size: $theme-txt-body;
  }
  .reactivate-view--long-grass-policy {
    color: $theme-color-blue;
  }
  .reactivate-view--question-circle {
    color: $theme-color-white;
    background: $theme-color-blue;
  }
  .reactivate-view--preferred-start-datepicker {
    border-color: $theme-color-primary-light;
  }
  .reactivate-view--payment {
    border-color: $theme-color-primary-light;
  }
  .reactivate-view--cycle-item {
    background-color: $theme-color-primary-light;
    color: $theme-color-primary;
    strong {
      color: $theme-color-primary;
    }
  }
  .reactivate-view--selected-cycle-item {
    background-color: $theme-color-primary;
    color: $theme-color-white;
  }
  .reactivate-view--selected-cycle-text {
    color: $theme-color-white;
    strong {
      color: $theme-color-white;
    }
  }
  .reactivate-view--cycle-text {
    color: $theme-color-primary;
  }
  .reactivate-view--address-card {
    border-color: $theme-color-primary-light;
  }
  .reactivate-view--itemized-charges {
    border-color: $theme-color-primary-light;
  }
  .reactivate-view--charges-total {
    border-color: $theme-color-primary-light;
  }
  .reactivate-view--total-text {
    color: $theme-color-primary;
  }
  @media (max-width: $mobile-max-width) {
    .reactivate-view--main-header {
      border-color: $theme-color-primary-light;
    }
    .reactivate-view--card-ending-last-4 {
      font-size: $theme-txt-body-sm;
    }
    .reactivate-view--cycle-container {
      border-color: $theme-color-primary-light;
    }
    .reactivate-view--address-card {
      border-color: $theme-color-primary-light;
    }
    .reactivate-view--special_deal {
      border-color: $theme-color-primary-light;
    }
  }
}
</style>
